import { styled } from "goober";
import {
  type ReactNode,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { smoothPath, type Point } from "../../lib/smoothPath";
import {
  ChartMarker,
  ChartPill,
  ChartTooltip,
  ChartTooltipCount,
  ChartTooltipDot,
  ChartTooltipHeader,
  ChartTooltipRow,
} from "../chartHover";
import { GhostPill } from "../GhostPill";
import { Text } from "../Text";
import type { DailyVolume } from "./connectorTraffic";
import { Panel } from "./workflowsLayout";

export type RequestVolumeSeries = {
  key: string;
  label: string;
  color: string;
  // The folded "N other connectors" aggregate — always ranked last.
  isOther?: boolean;
};

export type OverviewStat = {
  label: string;
  value: number;
  format: (value: number) => string;
};

const DAY = 86_400_000;
const VIEW_W = 960;
const VIEW_H = 200;
const PAD_X = 24;
const PAD_TOP = 16;
const PAD_BOTTOM = 0;
const BASE_Y = VIEW_H - PAD_BOTTOM;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;
const PLOT_W = VIEW_W - PAD_X * 2;
// The plot baseline (SVG bottom, since PAD_BOTTOM is 0) sits this far above the
// ChartArea bottom. The 24px-tall "Today"/axis pills bottom-align there, leaving a
// 24px gap between the baseline (and its dashed projection) and the pills.
const AXIS_STRIP = 48;
const LINE = "var(--green-600)";
const LINE_DIM = "var(--green-400)";
const FUTURE = "var(--gray-400)";

// Empty-state skeleton: a fixed, muted curve standing in for "what this chart
// looks like with data", mirroring the ghost columns/rows on the other tabs.
// The path is constant, so build it once from a sample series.
const GHOST_COLOR = "var(--gray-400)";
const GHOST_LINE = [4, 6, 5, 8, 7, 6, 9, 11, 10, 13, 11, 14];
const GHOST_AXIS_PCT = [12, 31, 50, 69, 88];
const { curve: GHOST_CURVE, area: GHOST_AREA } = (() => {
  const max = Math.max(...GHOST_LINE);
  const gn = GHOST_LINE.length;
  const pts: Point[] = GHOST_LINE.map((v, i) => ({
    x: PAD_X + (i / (gn - 1)) * PLOT_W,
    y: BASE_Y - (v / max) * PLOT_H,
  }));
  const ext = [
    { x: 0, y: pts[0]!.y },
    ...pts,
    { x: VIEW_W, y: pts[pts.length - 1]!.y },
  ];
  const curve = smoothPath(ext);
  return { curve, area: `${curve}L${VIEW_W},${BASE_Y}L0,${BASE_Y}Z` };
})();

// A container so the two groups respond to the panel's own width (it's a grid
// column, so the viewport is the wrong axis to key off): once the stats and
// filters can't sit side by side as rows, each collapses into a vertical stack
// hugging its own edge, rather than the filters overflowing.
const Header = styled("div")`
  container-type: inline-size;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px 24px;
  flex-wrap: wrap;
`;

const StatsRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;

  @container (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const StatItem = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 64px;
  font-variant-numeric: tabular-nums;
`;

// Sits beside the stats when they fit; below the container breakpoint the
// pickers become a right-aligned vertical stack (min-width: 0 lets it shrink to
// each picker's width so nothing overflows).
const FilterRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
  justify-content: flex-end;

  @container (max-width: 640px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
`;

const ChartArea = styled("div")`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 180px;
`;

// An SVG with a viewBox has an intrinsic aspect ratio, so fixing only one axis
// lets the browser derive the other from it (pinning height alone makes the
// width a fixed viewBox-ratio value instead of filling). Pin both: width fills
// the container, height leaves AXIS_STRIP for the labels, and
// preserveAspectRatio="none" stretches the plot to fit.
const Svg = styled("svg")`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% - ${AXIS_STRIP}px);
  display: block;
  overflow: visible;
`;

// The placeholder line, softly faded at both edges so it reads as an ambient
// skeleton rather than a real, decisively-ending series.
const GhostSvg = styled(Svg)`
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black ${PAD_X}px,
    black calc(100% - ${PAD_X}px),
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    black ${PAD_X}px,
    black calc(100% - ${PAD_X}px),
    transparent
  );
`;

// Same box as ChartPill (runs-graph edge label) so a hover/now pill lands
// exactly on the axis label baseline; only the background differs.
const AxisLabel = styled("div")`
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  padding: 6px 8px;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 350;
  line-height: 12px;
  letter-spacing: -1%;
  white-space: nowrap;
  pointer-events: none;
`;

const TooltipLabel = styled("span")`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function formatDayLabel(day: number): string {
  return new Date(day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function labelStep(dayCount: number): number {
  if (dayCount <= 10) return 2;
  if (dayCount <= 40) return 7;
  return 14;
}

const TWEEN_MS = 350;
const STAT_TWEEN_MS = 180;
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

// Resample a value series to a new length by linear interpolation, so the line
// can tween between windows with different day counts instead of snapping.
function resampleSeries(values: readonly number[], length: number): number[] {
  if (values.length === length) return [...values];
  if (values.length === 0) return new Array(length).fill(0);
  if (length === 1) return [values[values.length - 1]!];
  return Array.from({ length }, (_, i) => {
    const pos = (i / (length - 1)) * (values.length - 1);
    const lo = Math.floor(pos);
    const hi = Math.min(values.length - 1, lo + 1);
    return values[lo]! + (values[hi]! - values[lo]!) * (pos - lo);
  });
}

// Eases a series of numbers toward its target whenever the values change, so
// the line and the stat numbers animate instead of snapping. `resample` lets
// the line morph across a changed day count; the fixed-length stats don't need
// it.
function useTweenedSeries(
  target: readonly number[],
  resample: boolean,
  durationMs: number
): number[] {
  const [displayed, setDisplayed] = useState<number[]>(() => [...target]);
  const displayedRef = useRef(displayed);
  displayedRef.current = displayed;
  const targetRef = useRef(target);
  targetRef.current = target;
  const signature = target.join("|");

  useLayoutEffect(() => {
    const to = [...targetRef.current];
    const current = displayedRef.current;
    const from =
      current.length === to.length
        ? current
        : resample
          ? resampleSeries(current, to.length)
          : to;
    if (from.length === to.length && from.every((v, i) => v === to[i])) {
      if (current !== from) setDisplayed(from);
      return;
    }
    setDisplayed(from);
    const start = performance.now();
    let raf = requestAnimationFrame(function tick(now) {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = easeOutCubic(t);
      setDisplayed(to.map((v, i) => from[i]! + (v - from[i]!) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [signature, resample, durationMs]);

  return displayed;
}

export function ConnectorRequestVolumePanel({
  volume,
  series,
  seriesKeyOf,
  stats,
  filters,
  nowDay,
  placeholder,
}: {
  volume: readonly DailyVolume[];
  series: readonly RequestVolumeSeries[];
  seriesKeyOf: ReadonlyMap<string, string>;
  stats: readonly OverviewStat[];
  filters: ReactNode;
  nowDay: number;
  placeholder?: boolean;
}) {
  const uid = useId();
  const gradId = `${uid}-fill`;
  const ghostGradId = `${uid}-ghost-fill`;
  const fadeGradId = `${uid}-fade-grad`;
  const fadeMaskId = `${uid}-fade`;
  const rightFadeGradId = `${uid}-rfade-grad`;
  const rightFadeMaskId = `${uid}-rfade`;
  const highlightClipId = `${uid}-highlight`;
  const chartRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const stacked = useMemo(
    () =>
      volume.map((day) => {
        const bySeries = new Map<string, number>();
        for (const [connectorId, requests] of day.byConnector) {
          const key = seriesKeyOf.get(connectorId) ?? connectorId;
          bySeries.set(key, (bySeries.get(key) ?? 0) + requests);
        }
        return { day: day.day, total: day.total, errors: day.errors, bySeries };
      }),
    [volume, seriesKeyOf]
  );

  // The line and the stat numbers tween toward new data (range/connector
  // changes) instead of snapping; the labels, markers, and tooltip snap.
  const targetTotals = useMemo(
    () => stacked.map((day) => day.total),
    [stacked]
  );
  const displayedTotals = useTweenedSeries(targetTotals, true, TWEEN_MS);
  const displayedStats = useTweenedSeries(
    stats.map((stat) => stat.value),
    false,
    STAT_TWEEN_MS
  );

  const n = stacked.length;
  const step = labelStep(n);
  const daySpacing = n > 1 ? PLOT_W / (n - 1) : PLOT_W;

  const xSvg = (i: number) => (n > 1 ? PAD_X + (i / (n - 1)) * PLOT_W : PAD_X);
  const xPct = (i: number) => (xSvg(i) / VIEW_W) * 100;

  // Days at or before "now" carry data (the line); anything after is the dashed
  // projection, mirroring the workflow runs graph.
  const now0 = Math.floor(nowDay / DAY) * DAY;
  let nowIndex = -1;
  for (let i = 0; i < n; i++) {
    if (stacked[i]!.day <= now0) nowIndex = i;
    else break;
  }
  if (nowIndex < 0) nowIndex = 0;
  const hasFuture = nowIndex < n - 1;
  const nowX = xSvg(nowIndex);

  const paths = useMemo(() => {
    if (n < 1) return null;
    const max = Math.max(1, ...displayedTotals);
    const yv = (v: number) => BASE_Y - (v / max) * PLOT_H;
    const pts: Point[] = [];
    for (let i = 0; i <= nowIndex; i++) {
      const x = n > 1 ? PAD_X + (i / (n - 1)) * PLOT_W : PAD_X;
      pts.push({ x, y: yv(displayedTotals[i] ?? 0) });
    }
    // Flat lead into the left edge (fade-in); extend to the right edge only when
    // there is no future region to hand off to the dashed projection.
    const ext = [
      { x: 0, y: pts[0]!.y },
      ...pts,
      ...(hasFuture ? [] : [{ x: VIEW_W, y: pts[pts.length - 1]!.y }]),
    ];
    const curve = smoothPath(ext);
    const endX = hasFuture ? nowX : VIEW_W;
    const area = `${curve}L${endX},${BASE_Y}L0,${BASE_Y}Z`;
    return { curve, area };
  }, [displayedTotals, n, nowIndex, hasFuture, nowX]);

  const hoveredIndex = stacked.findIndex((day) => day.day === hoveredDay);
  const hovered = hoveredIndex >= 0 ? stacked[hoveredIndex]! : null;

  const hoveredRows = hovered
    ? series
        .map((s) => ({ s, value: hovered.bySeries.get(s.key) ?? 0 }))
        .filter((row) => row.value > 0)
        // Rank by volume, but keep the "other" aggregate pinned to the bottom.
        .sort((a, b) =>
          a.s.isOther === b.s.isOther ? b.value - a.value : a.s.isOther ? 1 : -1
        )
    : [];

  // Position the tooltip from the marker's stable fraction, never its own
  // transitioning rect (measuring mid-animation gives a stale position): sit 8px
  // right of the marker, flipping to 8px left when the right side would clip.
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    const chart = chartRef.current;
    if (!el || !chart) return;
    const w = chart.clientWidth;
    const frac =
      (n > 1 ? PAD_X + (hoveredIndex / (n - 1)) * PLOT_W : PAD_X) / VIEW_W;
    const markerX = frac * w;
    const tipW = el.getBoundingClientRect().width;
    el.style.left = `${markerX + 8 + tipW > w ? markerX - 8 - tipW : markerX + 8}px`;
  }, [hoveredIndex, n]);

  if (placeholder) {
    return (
      <Panel>
        <Header>
          <StatsRow aria-hidden>
            {stats.map((stat) => (
              <StatItem key={stat.label}>
                <Text variant="text-small" color="var(--text-secondary)">
                  {stat.label}
                </Text>
                <GhostPill style={{ width: 44, height: 12, margin: "4px 0" }} />
              </StatItem>
            ))}
          </StatsRow>
          <FilterRow>{filters}</FilterRow>
        </Header>
        <ChartArea aria-hidden>
          <GhostSvg
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            preserveAspectRatio="none"
            fill="none"
          >
            <defs>
              <linearGradient id={ghostGradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GHOST_COLOR} stopOpacity="0.15" />
                <stop offset="60%" stopColor={GHOST_COLOR} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={GHOST_AREA} fill={`url(#${ghostGradId})`} />
            <path
              d={GHOST_CURVE}
              stroke={GHOST_COLOR}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </GhostSvg>
          {GHOST_AXIS_PCT.map((left) => (
            <AxisLabel key={left} style={{ left: `${left}%` }}>
              <GhostPill style={{ display: "block", width: 28 }} />
            </AxisLabel>
          ))}
        </ChartArea>
      </Panel>
    );
  }

  return (
    <Panel>
      <Header>
        <StatsRow>
          {stats.map((stat, i) => (
            <StatItem key={stat.label}>
              <Text variant="text-small" color="var(--text-secondary)">
                {stat.label}
              </Text>
              <Text variant="text-regular-md" color="var(--text-primary)">
                {stat.format(displayedStats[i] ?? stat.value)}
              </Text>
            </StatItem>
          ))}
        </StatsRow>
        <FilterRow>{filters}</FilterRow>
      </Header>
      <ChartArea ref={chartRef}>
        <Svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          fill="none"
          onMouseLeave={() => setHoveredDay(null)}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={LINE} stopOpacity="0.15" />
              <stop offset="60%" stopColor={LINE} stopOpacity="0" />
            </linearGradient>
            <linearGradient id={fadeGradId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="white" stopOpacity="0" />
              <stop
                offset={`${(PAD_X / VIEW_W) * 100}%`}
                stopColor="white"
                stopOpacity="1"
              />
              <stop
                offset={`${((VIEW_W - PAD_X) / VIEW_W) * 100}%`}
                stopColor="white"
                stopOpacity="1"
              />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <mask id={fadeMaskId}>
              <rect
                x="0"
                y="0"
                width={VIEW_W}
                height={VIEW_H}
                fill={`url(#${fadeGradId})`}
              />
            </mask>
            <linearGradient
              id={rightFadeGradId}
              gradientUnits="userSpaceOnUse"
              x1={nowX}
              y1="0"
              x2={VIEW_W}
              y2="0"
            >
              <stop offset="0%" stopColor="white" stopOpacity="1" />
              <stop offset="85%" stopColor="white" stopOpacity="1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <mask
              id={rightFadeMaskId}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width={VIEW_W}
              height={VIEW_H}
            >
              <rect
                x={nowX}
                y="0"
                width={VIEW_W - nowX}
                height={VIEW_H}
                fill={`url(#${rightFadeGradId})`}
              />
            </mask>
            <clipPath id={highlightClipId}>
              <rect
                x={-daySpacing}
                y="0"
                width={daySpacing * 2}
                height={VIEW_H}
                style={{
                  transform: `translateX(${xSvg(hoveredIndex >= 0 ? hoveredIndex : 0)}px)`,
                  transition:
                    "transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 50ms",
                }}
              />
            </clipPath>
          </defs>
          {paths && (
            <g mask={`url(#${fadeMaskId})`}>
              <path d={paths.area} fill={`url(#${gradId})`} />
              <path
                d={paths.curve}
                stroke={hovered ? LINE_DIM : LINE}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                style={{ transition: "stroke 150ms ease" }}
              />
              {hovered && (
                <path
                  d={paths.curve}
                  stroke={LINE}
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  clipPath={`url(#${highlightClipId})`}
                />
              )}
            </g>
          )}
          {hasFuture && (
            <g mask={`url(#${rightFadeMaskId})`}>
              <line
                x1={nowX}
                y1={BASE_Y}
                x2={VIEW_W}
                y2={BASE_Y}
                stroke={FUTURE}
                strokeWidth="3"
                strokeDasharray="6 4"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          )}
          {stacked.slice(0, nowIndex + 1).map((day, i) => (
            <rect
              key={day.day}
              x={xSvg(i) - daySpacing / 2}
              y={0}
              width={daySpacing}
              height={VIEW_H}
              fill="transparent"
              onMouseEnter={() => setHoveredDay(day.day)}
            />
          ))}
        </Svg>
        {stacked.map((day, i) =>
          i % step === 0 ? (
            <AxisLabel key={day.day} style={{ left: `${xPct(i)}%` }}>
              {formatDayLabel(day.day)}
            </AxisLabel>
          ) : null
        )}
        {hasFuture && (
          <>
            <ChartMarker
              style={{ left: `${xPct(nowIndex)}%`, top: 0, bottom: 0 }}
            />
            <ChartPill
              style={{
                left: `${xPct(nowIndex)}%`,
                bottom: 0,
                color: "var(--text-secondary)",
                background: FUTURE,
              }}
            >
              Today
            </ChartPill>
          </>
        )}
        {hovered && (
          <>
            <ChartMarker
              style={{
                left: `${xPct(hoveredIndex)}%`,
                top: 0,
                bottom: 0,
                background: "var(--text-primary)",
                transition: "left 150ms ease",
              }}
            />
            <ChartPill
              style={{
                left: `${xPct(hoveredIndex)}%`,
                bottom: 0,
                background: "var(--text-primary)",
                color: "var(--text-invert)",
                transition: "left 150ms ease",
              }}
            >
              {formatDayLabel(hovered.day)}
            </ChartPill>
            <ChartTooltip
              ref={tooltipRef}
              style={{
                width: 168,
                transition: "left 150ms ease",
              }}
            >
              <ChartTooltipHeader>Connectors</ChartTooltipHeader>
              {hoveredRows.map((row) => (
                <ChartTooltipRow key={row.s.key}>
                  <ChartTooltipDot style={{ background: row.s.color }} />
                  <TooltipLabel>{row.s.label}</TooltipLabel>
                  <ChartTooltipCount>
                    {row.value.toLocaleString("en-US")}
                  </ChartTooltipCount>
                </ChartTooltipRow>
              ))}
            </ChartTooltip>
          </>
        )}
      </ChartArea>
    </Panel>
  );
}
